<template>
    <nav
        class="select-none"
        aria-label="Filter"
    >
        <div
            class="wrapper flex w-full justify-end rounded-md p-0.5 shadow-sm ring-1 ring-gray-300 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-blue-600 focus:ring-inset"
        >
            <div :class="[useTags ? 'w-1/3' : 'w-full', 'flex']">
                <input
                    id="search"
                    v-model="pageData.search"
                    type="text"
                    name="search"
                    class="mr-1 block w-full rounded-md border-0 py-1.5 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
                    :placeholder="t('search')"
                    @keyup.enter="triggerFilter()"
                />
            </div>

            <div
                v-if="useTags"
                class="flex grow"
                @click.stop
            >
                <div class="w-full">
                    <input
                        id="tag-search"
                        v-model="tagFilterConfig.subject"
                        type="text"
                        name="tag-search"
                        class="mr-1 w-full rounded-md border-0 py-1.5 text-gray-900 placeholder:text-gray-400 focus:ring-0 focus:ring-offset-0 sm:text-sm sm:leading-6"
                        :placeholder="t('tagEnter')"
                        @blur="blur"
                        @click="filterSuggestions()"
                        @keyup="filterSuggestions()"
                    />
                    <div class="relative w-full">
                        <Transition
                            :show="tagFilterConfig.showSuggestions"
                            enter-active-class="duration-300 ease-out"
                            enter-from-class="transform opacity-0"
                            enter-to-class="opacity-100"
                            leave-active-class="duration-200 ease-in"
                            leave-from-class="opacity-100"
                            leave-to-class="transform opacity-0"
                        >
                            <div
                                v-if="tagFilterConfig.showSuggestions"
                                class="absolute top-px z-3000 m-0 flex w-full flex-wrap items-start gap-2 rounded-b-md border border-solid border-gray-300 bg-white p-4"
                            >
                                <div
                                    v-for="(tag, index) in tagFilterConfig.suggested"
                                    :key="index"
                                    v-tippy="t('buttonAddTag')"
                                    class="my-1 inline-block"
                                    @mousedown="addTag(tag)"
                                >
                                    <span
                                        class="cursor-pointer rounded-sm px-2 py-0.5 text-xs font-medium hover:opacity-75"
                                        :style="getTagStyles(tag)"
                                    >
                                        {{ tag.name }}
                                    </span>
                                </div>
                            </div>
                        </Transition>
                    </div>
                </div>
            </div>
            <div
                v-for="filter in Object.keys(pageData.filters)"
                :key="filter"
                class="flex-none rounded-md border-l border-l-gray-200"
            >
                <select
                    :id="`filter-${filter}`"
                    v-model="pageData.filters[filter].active"
                    v-tippy="pageData.filters[filter].name"
                    :name="`filter-${filter}`"
                    class="border-0 bg-white font-medium text-gray-600 outline-hidden outline-0 focus:border-0 focus:ring-0 focus:outline-0 sm:text-sm"
                >
                    <option
                        v-for="val in pageData.filters[filter].values"
                        :key="val.value"
                        class="font-medium"
                        :value="val.value"
                    >
                        {{ val.name }}
                    </option>
                </select>
            </div>
            <div class="flex-none rounded-md border-0">
                <button
                    v-tippy="t('search')"
                    type="button"
                    :class="[
                        buttons.length > 0 ? 'rounded-0' : 'rounded-r-md',
                        'relative inline-flex cursor-pointer items-center justify-end bg-white px-2 py-2 text-gray-400 ring-0 hover:bg-gray-200 hover:text-gray-600',
                    ]"
                    @click="triggerFilter()"
                >
                    <MdiMagnify
                        class="h-5 w-5"
                        aria-hidden="true"
                    />
                    <span class="sr-only">{{ t('search') }}</span>
                </button>
            </div>
            <div
                v-for="(item, index) in buttons"
                v-show="item.show"
                :key="index"
                class="flex-none rounded-md border-0"
            >
                <button
                    v-tippy="item.title"
                    type="button"
                    :class="[
                        index === buttons.length - 1 ? 'rounded-r-sm' : 'rounded-none',
                        'relative inline-flex cursor-pointer items-center justify-end bg-white px-2 py-2 text-gray-400 ring-0 hover:bg-gray-200 hover:text-gray-600',
                    ]"
                    @click="item.action"
                >
                    <component
                        :is="item.icon"
                        class="h-5 w-5 shrink-0"
                        aria-hidden="true"
                    />
                    <span class="sr-only">{{ item.title }}</span>
                </button>
            </div>
        </div>
        <div class="flex flex-wrap items-start gap-2 py-2">
            <div
                v-for="tag in tagFilterConfig.selected"
                :key="tag.tagId"
                class="flex cursor-default items-center justify-center rounded-sm bg-blue-600 px-1.5 py-0.5 text-xs font-medium text-gray-50"
                :style="getTagStyles(tag)"
            >
                <span class="-mt-px">{{ tag.name }}</span>
                <button
                    v-tippy="t('buttonRemoveTag')"
                    class="ml-2 cursor-pointer border-none hover:opacity-75"
                    @click="removeTag(tag)"
                >
                    <MdiClose class="h-3 w-3" />
                </button>
            </div>
        </div>
    </nav>
</template>

<script setup>
import { reactive, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { MdiMagnify, MdiClose } from 'materialdesignicons-vue3/icons/';
import { t } from '../composables/i18n';
import { getTagByName, getTagStyles } from '../composables/useTags';

defineExpose({
    name: 'FilterToolbar',
});

const props = defineProps({
    pageData: {
        type: Object,
        default: () => {
            return {};
        },
    },
    buttons: {
        type: Array,
        default: () => {
            return [];
        },
    },
});

const router = useRouter();
const route = useRoute();
const pageData = props.pageData;
const useTags = pageData?.useTags || false;
const allTags = pageData?.tags?.items || [];

const tagFilterConfig = reactive({
    showSuggestions: false,
    suggested: [],
    subject: '',
    selected: [],
});

tagFilterConfig.selected =
    (pageData?.selectedTags || []).map((tagName) => getTagByName(tagName, allTags)) || [];

const triggerFilter = () => {
    //console.log('Filter triggered', pageData.search);
    const query = { ...route.query };
    if (pageData.search && pageData.search.length > 2) {
        query.search = pageData.search;
    } else {
        delete query.search;
    }
    for (const filterKey of Object.keys(pageData.filters)) {
        const filterValue = pageData.filters[filterKey].active;
        if (filterValue && filterValue.length > 0) {
            query[filterKey] = filterValue;
        } else {
            delete query[filterKey];
        }
    }
    if (useTags) {
        if (tagFilterConfig.selected.length > 0) {
            query.tag = tagFilterConfig.selected.map((tag) => tag.name);
        } else {
            delete query.tag;
        }
    }
    router.push({
        name: route.name,
        params: {
            ...route.params,
            page: '1',
        },
        query,
    });
};

const blur = () => {
    tagFilterConfig.showSuggestions = false;
};

const filterSuggestions = () => {
    tagFilterConfig.showSuggestions = true;
    tagFilterConfig.suggested = allTags.filter((item) =>
        item.name.toLowerCase().includes(tagFilterConfig.subject.toLowerCase()),
    );
};

const removeTag = async (tag) => {
    if (isUsedTag(tag)) {
        tagFilterConfig.selected.splice(
            tagFilterConfig.selected.findIndex((item) => item.tagId === tag.tagId),
            1,
        );
    }
};

const addTag = async (tag) => {
    if (!isUsedTag(tag)) {
        tagFilterConfig.selected.push(tag);
    }
};

const isUsedTag = (tag) => {
    //console.log('isUsedTag:', tag.name, 'in', tagFilterConfig.selected);
    return tagFilterConfig.selected.some((item) => item.tagId === tag.tagId);
};

watch(
    () => tagFilterConfig.selected,
    (/* newTags */) => {
        //console.log('WATCH selectedTags:', newTags);
        triggerFilter();
    },
    { deep: true },
);
</script>

<style>
.slide-fade-enter-active {
    transition: all 200ms ease-out;
}

.slide-fade-leave-active {
    transition: all 200ms ease-in;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
    transform: translateY(100px);
    opacity: 0;
}
</style>
